import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { PageHeader } from '../../../components/PageHeader';
import { Button, HEADER_BUTTON_DARK_BLUE } from '../../../components/PageHeader/Button';
import { Switch2 } from '../../../components/Switch2/Switch2';
import Table3, { columnHelper } from '../../../containers/Table/Table3';
import SimpleModal from '../../../containers/Modal/SimpleModal';
import { notificationActions } from '../../../components/Notification/redux/actions';
import Strings from '../../../Strings';
import { actions, CREATE_CLINICIAN_ROSTER_RELEASE_RESULT, GET_CLINICIAN_ROSTER_CONFIGURATION_RESULT, PUBLISH_CLINICIAN_ROSTER_RELEASE_RESULT } from './actions';

import './ClinicianRosterConfiguration.scss';

const newMember = () => ({ npi: '', display_name: '', active_on_roster: true, medicare_enrolled: true });
const initialRelease = () => ({ version: '', source_note: '', effective_from: '', members: [newMember()] });

function ClinicianRosterConfiguration({ organizationId, organizationName, getConfiguration, createRelease, publishRelease, showNotification }) {
  const [configuration, setConfiguration] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [release, setRelease] = useState(initialRelease);
  const [isPublishing, setIsPublishing] = useState(false);

  const loadConfiguration = () => {
    setIsLoading(true);
    return getConfiguration(organizationId).then(response => {
      if (response.type === GET_CLINICIAN_ROSTER_CONFIGURATION_RESULT) {
        setConfiguration(response.response);
      } else {
        showNotification(Strings.accessClinicianRosterLoadFailed, 5000, true);
      }
    }).finally(() => setIsLoading(false));
  };

  useEffect(() => { loadConfiguration(); }, [organizationId]);

  const updateRelease = event => setRelease(current => ({ ...current, [event.target.name]: event.target.value }));
  const updateMember = (index, field, value) => setRelease(current => ({
    ...current,
    members: current.members.map((member, memberIndex) => memberIndex === index ? { ...member, [field]: value } : member),
  }));
  const addMember = () => setRelease(current => ({ ...current, members: [...current.members, newMember()] }));
  const removeMember = index => setRelease(current => ({ ...current, members: current.members.filter((_, memberIndex) => memberIndex !== index) }));
  const closeModal = () => { setModalOpen(false); setRelease(initialRelease()); };

  const publish = () => {
    setIsPublishing(true);
    createRelease(organizationId, release).then(created => {
      if (created.type !== CREATE_CLINICIAN_ROSTER_RELEASE_RESULT) throw new Error('create failed');
      return publishRelease(created.response.id);
    }).then(published => {
      if (published.type !== PUBLISH_CLINICIAN_ROSTER_RELEASE_RESULT) throw new Error('publish failed');
      showNotification(Strings.accessClinicianRosterPublished, 5000);
      closeModal();
      return loadConfiguration();
    }).catch(() => showNotification(Strings.accessClinicianRosterSaveFailed, 5000, true))
      .finally(() => setIsPublishing(false));
  };

  const roster = configuration?.release;
  const columns = [
    columnHelper.accessor('npi', { header: Strings.accessClinicianRosterNpi }),
    columnHelper.accessor('display_name', { header: Strings.accessClinicianRosterClinician }),
    columnHelper.accessor('active_on_roster', { header: Strings.accessClinicianRosterActive, cell: info => info.getValue() ? Strings.yes : Strings.no }),
    columnHelper.accessor('medicare_enrolled', { header: Strings.accessClinicianRosterMedicare, cell: info => info.getValue() ? Strings.yes : Strings.no }),
  ];
  const orgPrefix = organizationName ? `${organizationName}: ` : '';
  const validRelease = release.version.trim() && release.source_note.trim() && release.effective_from && release.members.length > 0
    && release.members.every(member => member.npi.trim() && member.display_name.trim());

  return (
    <>
      <PageHeader left={orgPrefix + Strings.accessClinicianRosterTab} />
      <div className="clinician-roster-configuration">
        <div className="clinician-roster-configuration__section-header">
          <div>
            <h2>{Strings.accessClinicianRosterCurrentRelease}</h2>
            {roster ? (
              <p>{`${roster.version} · ${Strings.accessClinicianRosterEffectiveFrom} ${roster.effective_from}`}</p>
            ) : <p>{Strings.accessClinicianRosterNotConfigured}</p>}
          </div>
          <Button
            class={HEADER_BUTTON_DARK_BLUE}
            title={Strings.accessClinicianRosterPublishNew}
            onClick={() => setModalOpen(true)}
          />
        </div>
        {roster && <p className="clinician-roster-configuration__source">{`${Strings.accessClinicianRosterSource}: ${roster.source_note}`}</p>}
      </div>
      <div className="clinician-roster-configuration__table">
        <Table3 name="access-clinician-roster" data={roster?.members || []} columns={columns} isLoading={isLoading} noPagination />
      </div>
      {modalOpen && (
        <SimpleModal
          name="access-clinician-roster-release"
          title={Strings.accessClinicianRosterPublishNew}
          onCancel={closeModal}
          onSubmit={publish}
          isSubmitDisabled={!validRelease || isPublishing}
          content={(
            <div className="clinician-roster-release-modal">
              <label htmlFor="access-clinician-roster-version">
                {Strings.accessClinicianRosterVersion}
                <input id="access-clinician-roster-version" name="version" value={release.version} onChange={updateRelease} />
              </label>
              <label htmlFor="access-clinician-roster-effective-from">
                {Strings.accessClinicianRosterEffectiveFrom}
                <input id="access-clinician-roster-effective-from" name="effective_from" type="date" value={release.effective_from} onChange={updateRelease} />
              </label>
              <label htmlFor="access-clinician-roster-source">
                {Strings.accessClinicianRosterSource}
                <textarea id="access-clinician-roster-source" name="source_note" value={release.source_note} onChange={updateRelease} />
              </label>
              <h3>{Strings.accessClinicianRosterMembers}</h3>
              {release.members.map((member, index) => (
                <div className="clinician-roster-release-modal__member" key={index}>
                  <input aria-label={Strings.accessClinicianRosterNpi} placeholder={Strings.accessClinicianRosterNpi} value={member.npi} onChange={event => updateMember(index, 'npi', event.target.value)} />
                  <input aria-label={Strings.accessClinicianRosterClinician} placeholder={Strings.accessClinicianRosterClinician} value={member.display_name} onChange={event => updateMember(index, 'display_name', event.target.value)} />
                  <div className="clinician-roster-release-modal__switch">
                    <span>{Strings.accessClinicianRosterActive}</span>
                    <Switch2 checked={member.active_on_roster} onChange={(_, checked) => updateMember(index, 'active_on_roster', checked)} />
                  </div>
                  <div className="clinician-roster-release-modal__switch">
                    <span>{Strings.accessClinicianRosterMedicare}</span>
                    <Switch2 checked={member.medicare_enrolled} onChange={(_, checked) => updateMember(index, 'medicare_enrolled', checked)} />
                  </div>
                  {release.members.length > 1 && <Button title={Strings.remove} onClick={() => removeMember(index)} />}
                </div>
              ))}
              <Button title={Strings.add} onClick={addMember} />
            </div>
          )}
        />
      )}
    </>
  );
}

ClinicianRosterConfiguration.propTypes = {
  organizationId: PropTypes.string.isRequired, organizationName: PropTypes.string,
  getConfiguration: PropTypes.func.isRequired, createRelease: PropTypes.func.isRequired,
  publishRelease: PropTypes.func.isRequired, showNotification: PropTypes.func.isRequired,
};

const mapDispatchToProps = dispatch => ({
  getConfiguration: organizationId => dispatch(actions.getConfiguration(organizationId)),
  createRelease: (organizationId, release) => dispatch(actions.createRelease(organizationId, release)),
  publishRelease: releaseId => dispatch(actions.publishRelease(releaseId)),
  showNotification: (message, timeout, error) => dispatch(notificationActions.show(message, timeout, error)),
});

export default connect(null, mapDispatchToProps)(ClinicianRosterConfiguration);
